import React, { useState, useRef } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Lightbox } from "./Lightbox";
import { ProductIllustration } from "./ProductIllustration";

export const ProductPhotos = ({ p, heightCls = "h-40" }) => {
  const imgs = (p.images && p.images.length ? p.images : [p.img]).filter(Boolean);
  const isRef = p.image_kind === "reference";
  const credit = (p.image_credit || isRef) ? {
    text: p.image_credit || "",
    source: p.image_source_url || "",
    license: p.image_license || "",
    licenseUrl: p.image_license_url || "",
    changes: (p.image_changes && !/^\s*unmodified\b/i.test(p.image_changes)) ? p.image_changes : "",
    reference: isRef,
  } : null;
  const refOverlay = isRef ? (
    <div data-testid={`reference-credit-${p.id}`}
         className="absolute inset-x-0 bottom-0 z-10 px-2 pb-1.5 pt-5"
         style={{ background: "linear-gradient(to top, rgba(13,58,43,0.94), rgba(13,58,43,0))" }}>
      <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-white">
        <span className="rounded px-1.5 py-0.5 text-[10px] font-bold" style={{ background: "rgba(0,0,0,0.4)" }}>Model reference image</span>
        <span className="text-[10px] font-semibold" style={{ color: "rgba(255,255,255,0.92)" }}>Actual unit photos pending</span>
      </div>
      {credit && credit.text && (
        <div className="mt-0.5 text-[9.5px] leading-tight" style={{ color: "rgba(255,255,255,0.85)" }}>
          Image:{" "}
          {credit.source
            ? <a href={credit.source} target="_blank" rel="noopener noreferrer nofollow" className="underline" onClick={(e) => e.stopPropagation()} data-testid={`reference-source-${p.id}`}>{credit.text}</a>
            : credit.text}
          {credit.license && credit.licenseUrl && (
            <> · <a href={credit.licenseUrl} target="_blank" rel="license noopener noreferrer nofollow" className="underline" onClick={(e) => e.stopPropagation()} data-testid={`reference-license-${p.id}`}>{credit.license}</a></>
          )}
          {credit.changes && <span style={{ opacity: 0.8 }}> · {credit.changes}</span>}
        </div>
      )}
    </div>
  ) : null;
  const [idx, setIdx] = useState(0);
  const [lbOpen, setLbOpen] = useState(false);
  const ref = useRef(null);

  const go = (i, e) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    const n = Math.max(0, Math.min(imgs.length - 1, i));
    const el = ref.current;
    if (el) el.scrollTo({ left: n * el.clientWidth, behavior: "smooth" });
    setIdx(n);
  };

  const onScroll = () => {
    const el = ref.current;
    if (el && el.clientWidth) setIdx(Math.round(el.scrollLeft / el.clientWidth));
  };

  const lightbox = lbOpen && (
    <Lightbox images={imgs} start={idx} alt={p.name} credit={credit} shareUrl={`${window.location.origin}/product/${p.id}`} onClose={() => setLbOpen(false)} />
  );

  if (imgs.length === 0) {
    return (
      <div className={`relative ${heightCls} w-full`} data-testid={`product-photo-pending-${p.id}`}>
        <ProductIllustration p={p} />
      </div>
    );
  }

  if (imgs.length <= 1) {
    const isRepresentative = !isRef && !(p.images && p.images.length);
    return (
      <div className={`relative ${heightCls} w-full`}>
        <img src={imgs[0]} alt={p.name} loading="lazy"
             onClick={() => setLbOpen(true)} data-testid={`product-photo-zoom-${p.id}`}
             className={`h-full w-full cursor-zoom-in ${isRef ? "object-contain" : "object-cover transition-transform duration-700 group-hover:scale-105"}`}
             style={{ filter: p.sold ? "grayscale(1)" : "none", opacity: p.sold ? 0.7 : 1 }} />
        {refOverlay}
        {isRepresentative && (
          <span data-testid={`representative-image-label-${p.id}`}
                className="absolute bottom-1.5 left-1.5 rounded px-1.5 py-0.5 text-[10px] font-semibold"
                style={{ background: "rgba(13,58,43,0.72)", color: "#fff", letterSpacing: "0.02em" }}>
            Representative image
          </span>
        )}
        {lightbox}
      </div>
    );
  }

  return (
    <div className={`relative ${heightCls} w-full`} data-testid={`product-photos-${p.id}`}>
      <div ref={ref} onScroll={onScroll}
           className="kp-photos flex h-full w-full snap-x snap-mandatory overflow-x-auto scroll-smooth">
        {imgs.map((src, i) => (
          <img key={i} src={src} alt={`${p.name} photo ${i + 1}`} loading="lazy"
               onClick={() => setLbOpen(true)}
               className={`h-full w-full shrink-0 cursor-zoom-in snap-center ${isRef ? "object-contain" : "object-cover"}`}
               style={{ filter: p.sold ? "grayscale(1)" : "none", opacity: p.sold ? 0.7 : 1 }} />
        ))}
      </div>
      <button onClick={(e) => go(idx - 1, e)} aria-label="Previous photo" data-testid={`photo-prev-${p.id}`}
              className={`absolute left-1.5 top-1/2 z-10 grid h-9 w-9 -translate-y-1/2 place-items-center rounded-full transition-opacity ${idx === 0 ? "opacity-0 pointer-events-none" : "opacity-90"}`}
              style={{ background: "rgba(255,253,247,0.92)", color: "var(--green-950)" }}>
        <ChevronLeft size={16} />
      </button>
      <button onClick={(e) => go(idx + 1, e)} aria-label="Next photo" data-testid={`photo-next-${p.id}`}
              className={`absolute right-1.5 top-1/2 z-10 grid h-9 w-9 -translate-y-1/2 place-items-center rounded-full transition-opacity ${idx === imgs.length - 1 ? "opacity-0 pointer-events-none" : "opacity-90"}`}
              style={{ background: "rgba(255,253,247,0.92)", color: "var(--green-950)" }}>
        <ChevronRight size={16} />
      </button>
      <div className="absolute bottom-2 left-1/2 z-10 flex -translate-x-1/2 gap-0.5">
        {imgs.map((_, i) => (
          <button key={i} onClick={(e) => go(i, e)} aria-label={`Photo ${i + 1}`}
                  className="grid h-6 place-items-center px-1">
            <span className="block h-1.5 rounded-full transition-all"
                  style={{ width: i === idx ? 14 : 6, background: i === idx ? "#E8501A" : "rgba(255,255,255,0.85)" }} />
          </button>
        ))}
      </div>
      {refOverlay}
      {lightbox}
    </div>
  );
};
